import React from 'react';
import { FileText, AlertTriangle, ExternalLink, Tag, Search, Calendar, ShieldAlert } from 'lucide-react';
export function BulletinsPage() {
  const bulletins = [{
    id: 'CVE-2026-1234',
    title: 'Critical RCE in Azure AD Connect',
    severity: 'Critical',
    published: 'Feb 10, 2026',
    source: 'Microsoft Security Response Center',
    description: 'A remote code execution vulnerability exists in Azure AD Connect when the Health agent is installed on a targeted server. An attacker who successfully exploits this vulnerability could run arbitrary code with SYSTEM privileges.',
    tags: ['Azure', 'Identity', 'Critical', 'RCE']
  }, {
    id: 'CVE-2026-5678',
    title: 'AWS IAM Role Privilege Escalation',
    severity: 'High',
    published: 'Feb 08, 2026',
    source: 'AWS Security Bulletin',
    description: 'Specific IAM policy configurations may allow users to assume roles with higher privileges than intended. Review all IAM policies granting sts:AssumeRole permissions.',
    tags: ['AWS', 'IAM', 'Privilege Escalation']
  }, {
    id: 'CVE-2026-9012',
    title: 'Kubernetes API Server Denial of Service',
    severity: 'Medium',
    published: 'Feb 05, 2026',
    source: 'Kubernetes Security',
    description: 'The Kubernetes API server is vulnerable to a denial of service attack via malformed HTTP/2 requests. Patches are available for versions 1.31+.',
    tags: ['Kubernetes', 'Container', 'DoS']
  }, {
    id: 'CVE-2026-3456',
    title: 'GCP Cloud Storage Public Access Misconfiguration',
    severity: 'High',
    published: 'Feb 01, 2026',
    source: 'Google Cloud Security',
    description: 'New default settings for Cloud Storage buckets may inadvertently expose data if legacy ACLs are used. Recommended to enforce uniform bucket-level access.',
    tags: ['GCP', 'Storage', 'Data Leak']
  }, {
    id: 'PSA-2026-001',
    title: 'OpenSSH Pre-Auth Double Free Vulnerability',
    severity: 'Critical',
    published: 'Jan 28, 2026',
    source: 'NIST NVD',
    description: 'A double free vulnerability in OpenSSH server (sshd) could allow an unauthenticated remote attacker to execute arbitrary code with root privileges.',
    tags: ['Linux', 'SSH', 'RCE']
  }];
  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'Critical':
        return 'text-astro-danger bg-astro-danger/10 border-astro-danger/20';
      case 'High':
        return 'text-astro-danger bg-astro-danger/10 border-astro-danger/20';
      case 'Medium':
        return 'text-astro-text-secondary bg-white/5 border-white/10';
      default:
        return 'text-astro-text-secondary bg-white/5 border-white/10';
    }
  };
  return <div className="max-w-7xl mx-auto px-6 py-8" data-id="element-824">
      {/* Header */}
      <div className="mb-8" data-id="element-825">
        <h1 className="text-3xl font-bold text-white mb-2" data-id="element-826">
          Security Bulletins
        </h1>
        <p className="text-astro-text-secondary text-lg" data-id="element-827">
          Latest security advisories and threat intelligence
        </p>
      </div>

      {/* Search */}
      <div className="mb-8" data-id="element-828">
        <div className="relative" data-id="element-829">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-astro-text-secondary" size={18} data-id="element-830" />

          <input type="text" placeholder="Search bulletins by CVE, keyword, or platform..." className="w-full bg-astro-card border border-astro-border rounded-lg pl-10 pr-4 py-3 text-sm text-white focus:outline-none focus:border-astro-green/50 transition-colors shadow-lg" data-id="element-831" />

        </div>
      </div>

      {/* Bulletins List */}
      <div className="space-y-4" data-id="element-832">
        {bulletins.map(bulletin => <div key={bulletin.id} className="group bg-astro-card border border-astro-border rounded-lg p-6 hover:border-astro-green/30 transition-all" data-id="element-833">

            <div className="flex flex-col md:flex-row gap-6 items-start" data-id="element-834">
              <div className="shrink-0" data-id="element-835">
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${bulletin.severity === 'Critical' ? 'bg-astro-danger/10 text-astro-danger' : 'bg-astro-card border border-astro-border text-astro-text-secondary'}`} data-id="element-836">

                  {bulletin.severity === 'Critical' ? <ShieldAlert size={24} data-id="element-837" /> : <FileText size={24} data-id="element-838" />}
                </div>
              </div>

              <div className="flex-1" data-id="element-839">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-2 mb-2" data-id="element-840">
                  <h3 className="text-lg font-bold text-white group-hover:text-astro-green transition-colors" data-id="element-841">
                    {bulletin.title}
                  </h3>
                  <span className={`px-2.5 py-0.5 rounded text-xs font-bold uppercase tracking-wider border w-fit ${getSeverityColor(bulletin.severity)}`} data-id="element-842">

                    {bulletin.severity}
                  </span>
                </div>

                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-astro-text-secondary mb-3 font-mono" data-id="element-843">
                  <span className="flex items-center gap-1.5" data-id="element-844">
                    <Calendar size={12} data-id="element-845" /> {bulletin.published}
                  </span>
                  <span className="hidden sm:block w-1 h-1 rounded-full bg-astro-border" data-id="element-846"></span>
                  <span data-id="element-847">{bulletin.source}</span>
                  <span className="hidden sm:block w-1 h-1 rounded-full bg-astro-border" data-id="element-848"></span>
                  <span data-id="element-849">{bulletin.id}</span>
                </div>

                <p className="text-sm text-astro-text-secondary/80 mb-4 leading-relaxed max-w-3xl" data-id="element-850">
                  {bulletin.description}
                </p>

                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mt-4 pt-4 border-t border-astro-border/50" data-id="element-851">
                  <div className="flex flex-wrap gap-2" data-id="element-852">
                    {bulletin.tags.map(tag => <span key={tag} className="flex items-center gap-1 px-2 py-1 rounded bg-[#0a0e14] border border-astro-border text-xs text-astro-text-secondary" data-id="element-853">

                        <Tag size={10} data-id="element-854" />
                        {tag}
                      </span>)}
                  </div>
                  <button className="flex items-center gap-1 text-sm font-medium text-astro-green hover:text-astro-green-dark transition-colors" data-id="element-855">
                    Read More <ExternalLink size={14} data-id="element-856" />
                  </button>
                </div>
              </div>
            </div>
          </div>)}
      </div>
    </div>;
}